import styled from 'styled-components/macro';
import Skeleton from "react-loading-skeleton";
import {useEffect, useState} from "react";
import {
    Button,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    Tooltip,
    useToast,
} from "@chakra-ui/react";
import {useActions, useLoggedIn, useModal, useRelativeTime, useSettings} from "../store";
import {useCommunities, useCommunitySubscribe, useModeratorList} from "../api/communities";
import {useFeedback} from "../store/feedback";
import {useBaseCommunity, useStoreCommunity, useUsername} from "../store/hooks";
import {formatDate} from "../utils/calendar";
import {isPseudoCommunity} from "../store/communites";
import {useBlock} from "../api/user";
import {useSavedFeeds, useSaveFeed} from "../api/custom_feeds";
import {FeedIcons, placeholderImage, SuggestedFeeds} from "../utils/feeds";
import SidebarList from "./sidebar_list";
import Community from "./community";
import NavLink from "./nav_link";
import CommunityLogo from "./community_logo";
import CreatePost from "./create_post";
import CreateMessage from "./create_message";
import Signup from "./signup";
import FlairUser from "./flair_user";
import CreateCommunity from "./create_community";
import CreateFeed from "./create_feed";
import FeedSettings from "./feed_settings";
import FeedConfig from "./sidebar/feed_config";
import RoleIndicator from "./role_indicator";
import ProSubscribe from "./pro_subscribe";
import {useHistory, useLocation} from "react-router-dom";
import classNames from "classnames";
import useHost from "../utils/host";
import GoogleAd from "./ads/GoogleAd";
import SidebarAdUnit from "./sidebar/sidebar_ad_unit";
import useWindowDimensions from "../utils/dimensions";
import WhizzcoSidebarUnit from "./ads/whizzco_sidebar_unit";
import ErrorBoundary from "./error_boundary";
import {themecolors} from "../utils/themecolors";
import {
    Calendar,
    ChevronDown,
    ChevronUp,
    CircleCheck, Clock,
    Delete as LucideDelete, EllipsisVertical, Flag, Search as LucideSearch,
    Mail, MessageCircle,
    Pencil, Settings,
    Star, UserRoundMinus
} from "lucide-react";

const Topics = styled.div``;
const Search = styled.div``;
const SearchIcon = styled.div``;
const Heading = styled.div``;
const Item = styled.div``;
const Icon = styled.div``;
const Number = styled.div``;
const Description = styled.div``;
const CommunitySidebar = styled.div``;
const Title = styled.div`
  margin-left: 8px;
  width: 100%;`;
const SidebarImage = styled.div``;
const Message = styled.div``;
const Created = styled.div``;
const Subscribe = styled.div``;
const Create = styled.div``;
const Content = styled.div``;
const UserFlair = styled.div``;
const CreateNewCommunity = styled.div``;
const LegalItems = styled.div``;
const LegalItem = styled.div``;
const LegalDisclaimer = styled.div``;
const SignUpButton = styled.div``;
const SignUpDescription = styled.div``;
const CreateFeedButton = styled.div``;
const More = styled.div``;
const Icons = styled.div``;
const EditFeed = styled.div``;
const ShowAll = styled.div``;
const Categories = styled.div``;
const Category = styled.div``;
const AdDescription = styled.div``;
const LegacyCreatePost = styled.div``;
const Body = styled.div``;

const TOPICS = ['All', 'Crypto', 'Politics', 'Sports', 'Gaming', 'Memes', 'Streaming', 'Movies', 'Humor', 'Finance'];

const ProPromo = styled(({className}) => {
    const modal = useModal();
    const {pro_tier} = useSettings();
    const history = useHistory();
    const handlePro = async () => {
        if (pro_tier === 0) {
            modal.open(ProSubscribe);
        } else {
            history.push('/settings?type=subscription');
        }
    }
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>{pro_tier === 0 ? 'Upgrade to Pro' : 'Pro Subscription'}</Heading>
            <Body className={'sidebar-body'}>
                <Stack spacing={2}>
                    <AdDescription>{pro_tier === 0 ? 'Subscribing to Pro is the best way to directly support the platform.' :
                        'Thanks for directly supporting the platform.'}</AdDescription>
                    <Button variant='grey' onClick={handlePro}>{pro_tier === 0 ? 'Learn More' : 'Manage'}</Button>
                </Stack>
            </Body>
        </SidebarList>
    )
})`
  ${Body} {
    padding: 0 16px;
  }
  ${AdDescription} {
    font-size: 14px;
  }

`;

const ModeratedCommunities = styled(({moderates, className, onClose}) => {
    const loading = useFeedback();
    const communities = useCommunities(loading.channel, moderates);
    const subscribing = useFeedback();
    const subscribe = useCommunitySubscribe(subscribing.channel);
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Moderator for...</Heading>
            <Body className={'sidebar-body'}>
                {communities?.map((community) => community && (
                    <NavLink react_route key={community?.name} href={`/c/${community?.name}`} onClick={() => onClose ? onClose() : false}>
                        <Community
                            key={community?.name}
                            community={community}
                            onSubscribe={subscribe}
                        />
                    </NavLink>
                ))}
                {loading.busy && Array(3).fill(null).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Item key={index}>
                        <Icon>
                            <Skeleton circle height={24} width={24}/>
                        </Icon>
                        <Title>
                            <Skeleton/>
                        </Title>
                    </Item>
                ))}
            </Body>
        </SidebarList>
    );
})`
  
  ${Item} {
    font-size: 15px;
    display: flex;
    align-items: center;
    padding: 8px 0;
    margin: 0 16px;
  }

  ${Item} img {
    margin-right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
  ${Community} {
    padding: 8px 16px;
  }
  ${Community}:hover {
    background: ${({theme}) => theme.color.name === 'dark' ? theme.dark_border : themecolors.light_input};
  }
`;

const SubscribedCommunities = styled(({className, onClose}) => {
    const loading = useFeedback();
    const {subscribed} = useSettings();
    const communities = useCommunities(loading.channel, subscribed);
    const subscribing = useFeedback();
    const subscribe = useCommunitySubscribe(subscribing.channel);
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Your Communities</Heading>
            <Body className={'sidebar-body'}>
                {communities?.map((community) => (
                    <NavLink react_route key={community?.name} href={`/c/${community?.name}`} onClick={() => onClose ? onClose() : false}>
                        <Community
                            key={community?.name}
                            community={community}
                            onSubscribe={subscribe}
                        />
                    </NavLink>
                ))}
            </Body>
        </SidebarList>
    );
})`

  ${Item} {
    font-size: 15px;
    display: flex;
    align-items: center;
    padding: 8px 0;
    margin: 0 16px;
  }

  ${Item} img {
    margin-right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
  ${Community} {
    padding: 8px 16px;
  }
  ${Community}:hover {
    background: ${({theme}) => theme.color.name === 'dark' ? theme.dark_border : themecolors.light_input};
  }
`;

const AllCommunitiesModule = styled(({className, onClose}) => {
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>More Communities</Heading>
            <Body className={'sidebar-body'}>
                <Categories>
                    {TOPICS?.map((topic) => (
                        <NavLink onClick={() => onClose ? onClose() : false} react_route key={topic} href={`/communities?topic=${topic.toLowerCase()}`}>
                            <Category>{topic}</Category>
                        </NavLink>
                    ))}
                </Categories>
            </Body>
        </SidebarList>
    );
})`
  position: relative;
  ${Categories} {
    margin: 0 16px;
    display: flex;
    flex-wrap: wrap;
  }
  ${Category} {
    font-size: 14px;
    padding: 4px 14px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? 'rgb(52 54 54)' : 'rgb(238 238 238)'};
    border-radius: 4px;
    margin: 0 4px 6px 0;
  }
`;

const PseudoCommunityInfo = styled(({communityName, className}) => {

    const modal = useModal();
    const loggedIn = useLoggedIn();

    const handleCreate = (event) => {
        event.preventDefault();
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(CreatePost);
        }
    };

    const feedType = communityName.toLowerCase() === 'home' ? 'Home Feed' : 'Trending Feed';

    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>
                {feedType}
            </Heading>
            <Body className={'sidebar-body'}>
                <Description>
                    {feedType === 'Home Feed' ? 'Home contains posts from your communities. To add posts from more communities, just subscribe.'
                        : 'Trending contains posts from all communities, but you can filter communities or entire topics.'}
                </Description>
                <div onClick={handleCreate}>
                    <NavLink href={'/submit'}>
                        <Button variant='blue' mt='6px' width={'100%'}>
                            Create Post
                        </Button>
                    </NavLink>
                </div>
            </Body>
        </SidebarList>
    );
})`
  ${Body} {
    padding: 0 16px;
  }
  ${Description} {
    font-weight: 400;
    font-size: 15px;
    margin-bottom: 12px;
  }
  ${Create} {
    cursor: pointer;
    width: 100%;
    border-radius: 32px;
    height: 40px;
    padding: 12px;
    text-align: center;
    line-height: initial;
    margin-bottom: 8px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? 'transparent' : themecolors.pure_white};
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : '#307ad5'};
    border: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : '#307ad5'};
    box-sizing: border-box;
  }
`;

const CommunityInfo = styled(({communityName, className}) => {

    const modal = useModal();
    const loading = useFeedback();
    const community = useCommunities(loading.channel, [communityName])[0] || false;
    const created = formatDate(community.created ? community.created : 0);
    const loggedIn = useLoggedIn();
    const subscribing = useFeedback();
    const subscribe = useCommunitySubscribe(subscribing.channel);
    const newPostLink = isPseudoCommunity(communityName) ? '/submit' : `/c/${communityName}/submit`;

    const handleSubscribe = async () => {
        try {
            await subscribe([community.name]);
        } catch {
        }
    }

    const handleCreate = (event) => {
        event.preventDefault();
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(CreatePost, {community: community.name});
        }
    };

    const handleFlair = () => {
        modal.open(FlairUser, {community: community.name});
    };

    return (
        <SidebarList className={className}>
            <NavLink href={`/c/${communityName}`} react_route>
                <Heading className={"sidebar-about-header"}>
                        <CommunityLogo name={communityName} />
                        {community.display_name}
                </Heading>
            </NavLink>
            <Body className={"sidebar-about-body"}>
            <Description>
                {community.description}
            </Description>
            <Topics>
                <Categories>
                    {community?.topics?.map((topic) => (
                        <Category key={topic} className={"category"}>{topic}</Category>
                    ))}
                </Categories>
            </Topics>
            <Created>
                Created {created}
            </Created>
            <div onClick={handleCreate}>
                <NavLink href={`/c/${community ? community.name : communityName}/submit`}>
                    <Button variant='blue' mt='6px' width={'100%'}>
                        Create Post
                    </Button>
                </NavLink>
            </div>
            <Button variant='grey' mt='6px' width={'100%'} onClick={handleSubscribe}>
                {community.is_subscribed ? 'Unsubscribe' : 'Subscribe'}
            </Button>
            </Body>
        </SidebarList>
    );
})`
  ${Body} {
    padding: 0 16px;
  }
  ${Heading} {
    display: flex;
    align-items: center;
    font-size: 18px !important;
  }
  ${Heading} img {
    margin-right: 16px;
    border-radius: 50%;
  }
  font-size: 14px;
  ${Description} {
    font-weight: 400;
    font-size: 15px;
    margin-bottom: 12px;
  }
  ${Created} {
    display: flex;
    align-items: center;
    padding-bottom: 12px;
    font-weight: 400;
  }
  ${Created} svg,
  ${UserFlair} svg {
    margin-right: 6px;
  }
  ${Subscribe}, ${Create} {
    cursor: pointer;
    width: 100%;
    border-radius: 32px;
    height: 40px;
    padding: 12px;
    text-align: center;
    line-height: initial;
    margin-bottom: 8px;
  }
  ${Subscribe} {
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : '#307ad5'};
    color: ${({theme}) => theme.color.name === 'dark' ? theme.pure_black : themecolors.pure_white};
  }
  ${Create} {
    background-color: ${({theme}) => theme.color.name === 'dark' ? 'transparent' : themecolors.pure_white};
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : '#307ad5'};
    border: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : '#307ad5'};
    box-sizing: border-box;
  }
  ${UserFlair} {
    padding-top: 12px;
    cursor: pointer;
  }
  ${Topics} {
    margin: 12px 0;
  }
  ${Topics} > * {
    margin-right: 6px;
    margin-bottom: 6px;
  }
  ${Heading} img {
    width: 32px;
    height: 32px;
  }
  ${Categories} {
    display: flex;
    flex-wrap: wrap;
  }
  ${Category} {
    font-weight: 400;
    font-size: 14px;
    padding: 3px 12px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? 'rgb(52 54 54)' : 'rgb(238 238 238)'};
    border-radius: 48px;
    margin: 0 6px 6px 0;
  }
`;

const CommunityModsModule = styled(({className, community, onClose}) => {
    const loading = useFeedback();
    const mods = useModeratorList(loading.channel, community);
    const loggedIn = useLoggedIn();
    const modal = useModal();
    const handleMessage = () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(CreateMessage, {recipient: `c:${  community}`});
        }
    };
    const [showAll, setShowAll] = useState(false);
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Moderators <Message onClick={handleMessage}><Mail size={16} /></Message></Heading>
            <Body className={'sidebar-body'}>
                {mods?.map((mod, index) => (showAll || index < 8) && (
                    <Item key={mod.author}>
                        <NavLink react_route onClick={() => onClose ? onClose() : false} href={`/u/${mod.author}`}>{mod.author}</NavLink>
                    </Item>
                ))}
                {mods?.length > 8 &&
                <ShowAll onClick={() => setShowAll(!showAll)}>
                    {showAll ? 'Close full list' : `View ${mods.length - 8} more`}
                    {showAll ?
                        <ChevronUp size={16} /> :
                        <ChevronDown size={16} />
                    }
                </ShowAll>
                }
            </Body>
        </SidebarList>
    );
})`
  ${Body} {
    padding: 0 16px;
  }
  ${Item} {
    font-size: 15px;
    display: flex;
    align-items: center;
    padding: 2px 0;
  }
  ${Message} {
    float: right;
    cursor: pointer;
  }
  ${ShowAll} {
    display: flex;
    align-items: center;
    padding-top: 4px;
    cursor: pointer;
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
  }
  ${ShowAll} svg {
    margin-left: 4px;
  }
`;

const ModeratorsModule = styled(({className, community, onClose}) => {
    const isBaseCommunity = useBaseCommunity() === community;
    const baseLink = `${isBaseCommunity ? '' : `/c/${community}`}/`;
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Moderation</Heading>
            <Body className={'sidebar-body'}>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}logs`} react_route>Moderation Logs</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}queue`} react_route>Moderation Queue</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}modmail`}>Modmail</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}ban`} react_route>Ban Management</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}comments`} react_route>Comment Feed</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/settings`} react_route>Manage Settings</NavLink></Item>
                {/*<Item><NavLink href={`${baseLink}config/menu`} react_route>Manage Menu</NavLink></Item>*/}
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/reports`} react_route>Manage Rules</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/sidebar`} react_route>Manage Sidebar</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/stylesheet`} react_route>Manage Stylesheet</NavLink></Item>
                {/*<Item><NavLink href={`${baseLink}config/resources`} react_route>Manage Resources</NavLink></Item>*/}
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/post-flairs`} react_route>Manage Post Flairs</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/filter`} react_route>Manage Community Filter</NavLink></Item>
                <Item><NavLink onClick={() => onClose ? onClose() : false} href={`${baseLink}config/moderators`} react_route>Manage Moderators</NavLink></Item>
            </Body>
        </SidebarList>
    );
})`

  ${Body} {
    padding: 0 16px;
  }

  ${Item} {
    font-size: 14px;
    display: flex;
    align-items: center;
    padding: 6px 0;
  }

  ${Item} img {
    margin-right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
`;

const ProfileModule = styled(({className, profile, baseCommunity}) => {
    const age = useRelativeTime(profile.created, false);
    const modal = useModal();
    const loggedIn = useLoggedIn();
    const username = useUsername();
    const {feedbackCh} = useFeedback();

    const handleMessage = (target) => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(CreateMessage, {recipient: target});
        }
    };
    const block = useBlock(feedbackCh);
    const handleBlock = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                await block(profile.username);
            } catch {}
        }
    };
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>
                <span>
                    About {profile.username}
                </span>
                {profile.is_admin && <RoleIndicator admin /> }
                {baseCommunity !== "TheDonald" && profile.username !== username &&
                    <Menu>
                        <MenuButton
                            style={{marginLeft: 'auto', marginTop: '-6px'}}
                            as={IconButton}
                            aria-label="Options"
                            variant="none"
                            icon={<EllipsisVertical size={18} />}
                        />
                        <MenuList>
                            <MenuItem icon={<UserRoundMinus size={16} />} onClick={handleBlock}>
                                Block user
                            </MenuItem>
                            <MenuItem icon={<MessageCircle size={16} />}
                                      onClick={() => handleMessage(profile.username)}>
                                Message user
                            </MenuItem>
                            <MenuItem icon={<Flag size={16} />} onClick={() => handleMessage("c:win")}>
                                Report User
                            </MenuItem>
                        </MenuList>
                    </Menu>
                }
            </Heading>
            <Body className={'sidebar-body'}>
                <Content>
                    <Item>
                        <Clock size={22} />
                        <Number>{age}</Number>
                        <Description>Joined</Description>
                    </Item>
                    <Item>
                        <Pencil size={22} />
                        <Number>{profile.post_score}</Number>
                        <Description>Post Score</Description>
                    </Item>
                    <Item>
                        <MessageCircle size={22} />
                        <Number>{profile.comment_score}</Number>
                        <Description>Comment Score</Description>
                    </Item>
                </Content>
            </Body>
        </SidebarList>
    )
})`
  
  ${Heading} {
    display: flex;
  }
  ${Body} {
    padding: 0 16px;
  }
  ${Content} {
    display: flex;
    justify-content: space-between;
  }
  
  ${Item} {
    font-size: 13px;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }

  ${Content} svg {
    font-size: 24px;
  }
  
  ${Content} svg {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.light_medium_impact_text : themecolors.light_low_impact_text};
    margin-bottom: 12px;
  }
  
  ${Heading} svg {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.light_medium_impact_text : themecolors.light_low_impact_text};
  }

  ${Number} {
    font-size: 16px;
    margin-bottom: 4px;
  }
  ${RoleIndicator} {
    padding-left: 4px;
  }

`;

const LegalModule = styled(({className}) => {
    return (
        <div className={`${className} legal`}>
            <Stack spacing={2}>
                <LegalItems>
                    <LegalItem><NavLink href={'https://help.scored.co/knowledge-base/content-policy/'}>Content Policy</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://help.scored.co/knowledge-base/terms-of-service/'}>Terms of Service</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://help.scored.co/knowledge-base/privacy-policy/'}>Privacy Policy</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://help.scored.co/knowledge-base/how-can-i-advertise-on-the-platform/'}>Advertise</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://help.scored.co/'}>Help Center</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://docs.scored.co/'}>API Docs</NavLink></LegalItem>
                    <LegalItem><NavLink href={'https://help.scored.co/knowledge-base/submit-a-dmca-claim/'}>DMCA</NavLink></LegalItem>
                </LegalItems>
                <LegalDisclaimer>
                    Scored &copy; 2025. All rights reserved.
                </LegalDisclaimer>
            </Stack>
        </div>
    )
})`
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  ${LegalItems} {
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
  }
  ${LegalItem} {
    width: 130px;
    margin-bottom: 6px;
  }
  ${LegalDisclaimer} {
    font-size: 12px;
    text-align: center;
  }
  
`;

const SignupModule = styled(({className}) => {

    const modal = useModal();
    const handleSignup = () => {
        modal.open(Signup);
    };

    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Create an Account</Heading>
            <Body className={'sidebar-body'}>
                <SignUpDescription>Sign up to join communities, create posts and leave comments.</SignUpDescription>
                <Button width="100%" variant={'blue'} mt={'16px'} onClick={handleSignup}>Sign Up</Button>
            </Body>
        </SidebarList>
    )
})`
  
  ${Body} {
    padding: 0 16px;
  }
  ${SignUpButton} {
    background-color: #307ad5;
    color: white;
    width: 120px;
    margin: 0 auto;
    margin-top: 18px;
    border-radius: 40px;
    text-align: center;
    padding: 8px 16px;
    font-weight: 500;
    cursor: pointer;
  }
  ${SignUpDescription} {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
`;

const SetActiveFeed = styled.div``;
const ActiveFeed = styled.div``;
const SavedFeed = styled.div``;

const TopicsModule = styled(({className}) => {

    const loggedIn = useLoggedIn();
    const username = useUsername();

    const modal = useModal();
    const handleCreate = () => {
        modal.open(CreateFeed);
    };

    const actions = useActions();
    const handleActiveFeed = async (id) => {
        actions.settingsChanged({active_feed: id});
    };

    const {error, feedbackCh} = useFeedback();
    const saveFeed = useSaveFeed(feedbackCh);
    const toast = useToast();
    const handleUnsave = async (id) => {
        try {
            await saveFeed(id, false);
            toast({
                title: `You unsaved the feed.`,
                status: 'success',
                isClosable: true,
            })
            actions.savedFeedsRemoved(id);
        } catch {
            // error is displayed
        }
    };

    const handleEdit = (feed) => {
        modal.open(FeedSettings, {currentFeed: feed});
    };

    const feeds = useSavedFeeds(loggedIn);
    const {active_feed} = useSettings();

    const [showAll, setShowAll] = useState(false);

    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Explore Feeds</Heading>
            <Body className={'sidebar-body'}>
                <Stack spacing={2}>

                    {SuggestedFeeds?.map((feed, index) => (index < 3 || showAll) && (
                        // eslint-disable-next-line react/no-array-index-key
                        <NavLink key={index} href={`/feed/${feed.id}/${feed.name.toLowerCase()}`} react_route>
                            <Item>
                                <Icon>
                                    {FeedIcons[feed.name] || placeholderImage}
                                </Icon>
                                <p>{feed.name}</p>
                                <Icons>
                                    {loggedIn && active_feed === feed.id &&
                                        <Tooltip label={'This is your default feed.'}>
                                            <ActiveFeed><CircleCheck size={16} /></ActiveFeed>
                                        </Tooltip>
                                    }
                                    {loggedIn && active_feed !== feed.id &&
                                        <Tooltip label={'Set this as your default feed.'}>
                                            <SetActiveFeed onClick={() => handleActiveFeed(feed.id)}><CircleCheck size={16} /></SetActiveFeed>
                                        </Tooltip>
                                    }
                                </Icons>
                            </Item>
                        </NavLink>
                    ))}

                    <ShowAll onClick={() => setShowAll(!showAll)}>
                        {showAll ? 'Close full list' : `Show all 21`}
                        {showAll ?
                            <ChevronUp size={16} /> :
                            <ChevronDown size={16} />
                        }
                    </ShowAll>

                    {feeds?.length > 0 && <hr />}

                    {feeds?.map((feed, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <NavLink key={index} href={`/feed/${feed.id}/${feed.slug}`} react_route>
                            <Item>
                                <Icon>
                                    {FeedIcons[feed.feed_name] || placeholderImage}
                                </Icon>
                                <p>{feed.feed_name}</p>
                                <Icons>
                                    {loggedIn && feed.author === username &&
                                        <Tooltip label={'Configure this feed.'}>
                                            <EditFeed onClick={() => handleEdit(feed)}>
                                                <Settings size={16} />
                                            </EditFeed>
                                        </Tooltip>
                                    }
                                    {loggedIn && active_feed === feed.id &&
                                    <Tooltip label={'This is your default feed.'}>
                                        <ActiveFeed><CircleCheck size={16} /></ActiveFeed>
                                    </Tooltip>
                                    }
                                    {loggedIn && active_feed !== feed.id &&
                                    <Tooltip label={'Set this as your default feed.'}>
                                        <SetActiveFeed onClick={() => handleActiveFeed(feed.id)}><CircleCheck size={16} /></SetActiveFeed>
                                    </Tooltip>
                                    }
                                    <Tooltip label={'You saved this feed.'}>
                                        <SavedFeed onClick={() => handleUnsave(feed.id)}>
                                            <Star fill={"ffbe00"} color={"ffbe00"} size={16} />
                                        </SavedFeed>
                                    </Tooltip>
                                </Icons>
                            </Item>
                        </NavLink>
                    ))}

                    {loggedIn && feeds?.length > 0 &&
                        <More>
                            <NavLink href={'/feeds'} react_route>
                                Explore more feeds...
                            </NavLink>
                        </More>
                    }

                </Stack>
                {loggedIn &&
                    <div style={{padding: '0 16px'}}>
                        <Button width={"100%"} mt='12px' variant='grey' onClick={handleCreate}>Create Feed</Button>
                    </div>
                }
            </Body>
        </SidebarList>
    )
})`
  
  ${ShowAll} {
    display: flex;
    align-items: center;
    padding: 4px 16px 0 16px;
    cursor: pointer;
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
  }
  ${ShowAll} svg {
    margin-left: 4px;
  }
  
  ${More} {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
    padding: 0 16px;
  }
  
  ${Item} {
    padding: 8px 16px;
    font-size: 14px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }
  
  ${Item} ${Icon} svg,
  ${Item} ${Icon} img {
    margin-right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
  ${Icons} {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
  ${Icons} > * {
    padding: 0 4px;
  }
  ${Item}:hover ${EditFeed}, ${Item}:hover ${SetActiveFeed} {
    opacity: 1;
  }

  ${Item}:hover {
    background: ${({theme}) => theme.color.name === 'dark' ? theme.dark_border : themecolors.light_input};
  }

  ${CreateFeedButton} {
    background-color: #307ad5;
    color: white;
    width: 160px;
    margin: 0 auto;
    margin-top: 18px;
    border-radius: 40px;
    text-align: center;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
  }
  ${ActiveFeed} {
    color:  ${({theme}) => theme.placeholder_green_1};
  }
  ${EditFeed}, ${SetActiveFeed} {
    opacity: 0;
  }
`;

const Delete = styled.div``;
const Nothing = styled.div``;

const BlockedCommunitiesModule = styled(({className}) => {
    const {blocked_communities} = useSettings();
    const [items, setItems] = useState(blocked_communities);
    const [value, setValue] = useState('');
    const [showAll, setShowAll] = useState(false);
    const actions = useActions();
    const handleAdd = async (e) => {
        if (e.key === 'Enter') {
            if (value.length === 0) return;
            setItems([...items, value]);
            setValue('');
            await actions.settingsChanged({blocked_communities: [...items, value]});
        }
    };
    const handleDelete = async (value) => {
        setItems(
            items.filter((item) => {
                return item !== value;
            })
        );
        await actions.settingsChanged({blocked_communities:   items.filter((item) => {
                return item !== value;
            })});
    };
    useEffect(() => {
        setItems(blocked_communities);
    }, [blocked_communities]);
    return (
        <SidebarList className={className}>
            <Heading className={'sidebar-header'}>Blocked Communities</Heading>
            <Body className={'sidebar-body'}>
                {items && [...items].reverse().map((item, i) =>
                    (showAll || i < 2) && (
                    <Item key={i}>
                        {item}
                        <Delete onClick={() => handleDelete(item)}>
                            <LucideDelete size={16} />
                        </Delete>
                    </Item>
                    )
                )}
                {items.length === 0 &&
                    <Nothing>Nothing here yet.</Nothing>
                }
                {items.length > 2 &&
                    <ShowAll onClick={() => setShowAll(!showAll)}>
                        {showAll ? 'Close full list' : `Show all ${items.length}`}
                        {showAll ?
                            <ChevronUp size={16} /> :
                            <ChevronDown size={16} />
                        }
                    </ShowAll>
                }
                <Input placeholder={`Add a community...`}
                       value={value}
                       onChange={(e) => setValue(e.currentTarget.value)}
                       onKeyPress={(e) => handleAdd(e)}
                />
            </Body>
        </SidebarList>
    )
})`

  ${ShowAll} {
    display: flex;
    align-items: center;
    padding-top: 4px;
    cursor: pointer;
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
  }
  ${ShowAll} svg {
    margin-left: 4px;
  }
  
  ${Body} {
    padding: 0 16px;
  }
    
  input {
    font-size: 14px;
    margin-top: 12px;
  }
  ${Item} {
    display: flex;
    font-size: 14px;
  }
  ${Delete} {
    margin-left: auto;
    cursor: pointer;
  }
  ${Nothing} {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
`;

const Sidebar = styled(({className, profile, sidebar, topics, community, customFeed, showPseudoFeed, onClose}) => {

    const {pro_tier = 0, username} = useSettings();
    const baseCommunity = useBaseCommunity();
    const path = useLocation().pathname;

    const {error, feedbackCh} = useFeedback();
    const loggedIn = useLoggedIn();
    const storeCommunity = useStoreCommunity(community);
    const modal = useModal();

    const [query, setQuery] = useState('');
    const onInputChange = (event) => {
        setQuery(event.target.value);
    }
    const handleSubmit = (e) => {
        e.preventDefault();
        window.location.href = `/search?query=${query}${isPseudoCommunity(community) ? '' : `&community=${community}`}`;
    }

    const handleNewCommunity = () => {
        modal.open(CreateCommunity);
    };

    const handleNewPost = () => {
        modal.open(CreatePost, {community: baseCommunity, onClose});
    };

    const conditionIsMet = community === "abc124";
    const isScoredCo = useHost() === "scored.co";

    const adMap = [
        {image: 'https://media.scored.co/post/mUhMUUigolbz.jpeg', link: 'https://offers.proudpatriots.com/order-form-trumpinator-bear1?utm_source=affiliatetraffic&oid=136&_ef_transaction_id=&affid=76&oid=136&affid=76&creative_id=688'},
        {image: 'https://media.scored.co/post/MuGTyI1j4UqX.jpeg', link: 'https://offers.proudpatriots.com/order-form-trumpinator-bear1?utm_source=affiliatetraffic&oid=136&_ef_transaction_id=&affid=76&oid=136&affid=76&creative_id=688'},
        {image: 'https://media.scored.co/post/SoCO1dFB7q51.jpeg', link: 'https://offers.proudpatriots.com/order-form-trump-bill?utm_source=affiliatetraffic&oid=139&_ef_transaction_id=&affid=76&oid=139&affid=76&creative_id=703'},
      ];

    const [randomItem, setRandomItem] = useState(adMap[Math.floor(Math.random() * adMap.length)]);
    const [randomItem2, setRandomItem2] = useState(adMap[Math.floor(Math.random() * adMap.length)]);

    const {width} = useWindowDimensions();

    return (
        <aside className={classNames(className, `sidebar`)}>
            {width <= 780 || baseCommunity === "TheDonald" &&
                <Search className={"search"}>
                    <form onSubmit={handleSubmit}>
                        <InputGroup>
                            <InputLeftElement
                                pointerEvents="none"
                            ><LucideSearch size={20} color="rgb(160, 167, 174)"/></InputLeftElement>
                            <Input type="text" placeholder="Search" value={query} onChange={onInputChange}
                                   transitionDuration={'0'}/>
                        </InputGroup>
                    </form>
                </Search>
            }
            {!loggedIn && isPseudoCommunity(community) && <SignupModule />}
            {pro_tier === 0 && baseCommunity === "TheDonald" && loggedIn && <SidebarAdUnit />}
            {pro_tier === 0 && baseCommunity === "TheDonald" && !loggedIn &&
                <ErrorBoundary>
                    <WhizzcoSidebarUnit key={path} />
                </ErrorBoundary>
            }
{/*            {pro_tier === 0 && baseCommunity === "TheDonald" && <RumbleAdUnit />}*/}
{/*            {baseCommunity === "TheDonald" && !loggedIn && <div id="rc-widget-ab62e0" data-rc-widget data-widget-host="habitat" data-endpoint="//trends.revcontent.com" data-widget-id="274706" />}*/}
            {baseCommunity === "TheDonald" && loggedIn &&
                <LegacyCreatePost className="submit-text" onClick={handleNewPost}>
                    Create a Post
                </LegacyCreatePost>
            }
            {loggedIn && showPseudoFeed && community && isPseudoCommunity(community) && <PseudoCommunityInfo communityName={community} />}
            {baseCommunity !== "TheDonald" && community && !isPseudoCommunity(community) && <CommunityInfo communityName={community} />}
            {/*{isScoredCo && conditionIsMet && <GoogleAd adId="300x250"><div style={{marginBottom: '16px', textAlign: 'center'}} className="AdvallyTag" data-size="300x250" id="300x250" /></GoogleAd>}
            */}{/*{pro_tier === 0 && <SidebarDisplayAd community={community} />}*/}
            {community && (community === 'all' || community === 'trending')  && !customFeed && <BlockedCommunitiesModule />}
            {!isPseudoCommunity(community) &&
                <SidebarImage className={'sidebar-banner img'}/>
            }
     {/*       {baseCommunity === "TheDonald" && pro_tier === 0 &&
                <div className={"sponsored ad ad-unit google-ad googleAd google-sponsored-links ads4 fullSizeAd"} style={{marginBottom: '16px'}}>
                    <a href={randomItem2.link}>
                        <img src={randomItem2.image} />
                    </a>
                </div>
            }*/}
            {customFeed && <FeedConfig feed={customFeed} />}
            {topics && <TopicsModule />}
            {profile && <ProfileModule profile={profile} baseCommunity={baseCommunity}/>}
            {baseCommunity !== "TheDonald" && profile && profile.moderates.length !== 0 && !profile.is_admin && <ModeratedCommunities moderates={profile.moderates} onClose={onClose} />}
            {sidebar && <CommunitySidebar dangerouslySetInnerHTML={{__html: sidebar}}/>}
            {loggedIn && isPseudoCommunity(community) && <SubscribedCommunities onClose={onClose}/>}
            {baseCommunity !== "TheDonald" && loggedIn && <ProPromo />}
            {isPseudoCommunity(community) && <AllCommunitiesModule onClose={onClose}/>}
{/*            {baseCommunity === "TheDonald" && !loggedIn && <div id="rc-widget-c643bd" data-rc-widget data-widget-host="habitat" data-endpoint="//trends.revcontent.com" data-widget-id="274704" />}*/}
            {storeCommunity?.is_moderator && <ModeratorsModule community={community} onClose={onClose}/>}
            {community && !isPseudoCommunity(community) && <CommunityModsModule community={community} onClose={onClose}/>}
            {isScoredCo && conditionIsMet && <GoogleAd adId="AdvAd-RightSideRail"><div style={{marginBottom: '16px', textAlign: 'center'}} className="AdvallyTag" id="AdvAd-RightSideRail" /></GoogleAd>}
            {baseCommunity === "TheDonald" && !loggedIn &&
            <div className={"sponsored ad ad-unit google-ad googleAd google-sponsored-links ads4 fullSizeAd"} style={{marginBottom: '16px'}}>
                <a href={randomItem2.link}>
                    <img src={randomItem2.image} />
                </a>
            </div>
            }
            <LegalModule />
        </aside>
    )
})`
  flex: 0 0 300px;
  width: 300px;


  #rc-widget-ab62e0 .rc-widget-header,
  #rc-widget-c643bd .rc-widget-header {
    display: none !important;
  }
  #rc-widget-ab62e0 .rc-provider::before, 
  #rc-widget-c643bd .rc-provider::before {
    content: 'Ad by ';
  }
  #rc-widget-c643bd {
    margin-bottom: 12px;
  }
  ${LegacyCreatePost} {
    display: flex;
    height: 44px;
    align-items: center;
    justify-content: center;
    text-align: center;
    text-transform: uppercase;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.pure_white};
    font-size: 15px;
    cursor: pointer;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  }
  
  ${Search} {
    margin-bottom: 16px;
    z-index: 0;
  }

  ${Search} {
    position: relative;
  }
  
  ${Search} input {
    box-sizing: border-box;
    width: 100%;
    height: 40px;
    padding: 12px 12px 12px 38px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  }
  
  ${Search} input::placeholder {
    color: ${({theme}) => theme.color.name === 'dark' ? 'rgba(255,255,255,.6)' : '#a4b0c2'};
  }
  
  ${SearchIcon} {
    position: absolute;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    font-size: 16px;
    margin-top: 11px;
    margin-left: 12px;
  }

  ${CommunitySidebar} h3 {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    position: relative;
    padding: 10px 35px 8px;
    text-align: center;
    border-radius: 1.5px;
    line-height: 22px;
    margin: 0;
  }

  ${CommunitySidebar} h4 {
    margin: 0;
  }

  ${CommunitySidebar} blockquote {
    margin: 0 auto 14px;
    padding: 14px 12px 12px;
    font-size: 12px;
  }
  .theme-dark & ${CommunitySidebar} blockquote {
    background: ${({theme}) => themecolors.dark_background_2};
  }

  ${SidebarImage} {
    width: 100%;
    margin-bottom: 12px;
  }
  ${CreateNewCommunity} {
    margin-bottom: 16px;
  }
  ${CreateNewCommunity} svg  {
    margin-right: 12px;
  }

  ${Body} {
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
    padding-top: 4px;
    padding-bottom: 16px;
  }
  ${Heading} {
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
    padding: 16px 16px 8px 16px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    font-weight: 600;
    font-size: 16px;
  }
  
  @media only screen and (max-width: 780px) {
    & {
      display: none;
    }
  }
`;

export default Sidebar;
